import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, useMotionValue, useTransform, animate, AnimatePresence } from "framer-motion";
import { Star, X, Heart, Clock, Users, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SWIPE_THRESHOLD = 100;
const SESSION_DURATION = 90; // seconds

// Vibe tags based on cuisine/restaurant type
const VIBE_TAGS = {
  "American": ["Comfort food", "Classic vibes", "Group-friendly"],
  "Italian": ["Date night", "Cozy spot", "Pasta mood"],
  "Mexican": ["Fiesta time", "Quick bite", "Group-friendly"],
  "Asian": ["Trendy", "Late-night", "Quick bite"],
  "Indian": ["Spice adventure", "Group-friendly", "Flavor bomb"],
  "Mediterranean": ["Fresh & healthy", "Date night", "Light bites"],
  "Fast Food": ["Quick bite", "Late-night", "Budget-friendly"],
  "Cafe": ["Chill vibes", "Coffee date", "Work-friendly"],
  "Restaurant": ["Crowd pleaser", "Safe bet", "Group-friendly"],
  "Seafood": ["Fresh catch", "Date night", "Special occasion"],
};

function getVibeTag(cuisineType, isOpen) {
  const tags = VIBE_TAGS[cuisineType] || VIBE_TAGS["Restaurant"];
  let tag = tags[Math.floor(Math.random() * tags.length)];
  if (isOpen === false) tag = "Opens later";
  if (isOpen === true && Math.random() > 0.7) tag = "Open now";
  return tag;
}

function SwipeCard({ restaurant, onSwipe, isTop, socialSignal }) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-15, 15]);
  const likeOpacity = useTransform(x, [0, 100], [0, 1]);
  const nopeOpacity = useTransform(x, [-100, 0], [1, 0]);
  const scale = useTransform(x, [-200, 0, 200], [0.95, 1, 0.95]);

  const handleDragEnd = (_, info) => {
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    
    if (Math.abs(offset) > SWIPE_THRESHOLD || Math.abs(velocity) > 500) {
      const direction = offset > 0 ? "right" : "left";
      animate(x, direction === "right" ? 400 : -400, {
        duration: 0.2,
        onComplete: () => onSwipe(direction),
      });
    } else {
      animate(x, 0, { type: "spring", stiffness: 500, damping: 30 });
    }
  };

  const vibeTag = getVibeTag(restaurant.cuisine_type, restaurant.is_open);
  const priceDisplay = restaurant.price_level ? "$".repeat(restaurant.price_level) : "$$";

  return (
    <motion.div
      className="absolute inset-0 rounded-3xl overflow-hidden bg-white shadow-2xl touch-pan-y select-none cursor-grab active:cursor-grabbing"
      style={{ x, rotate, scale, zIndex: isTop ? 10 : 0 }}
      drag={isTop ? "x" : false}
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      dragElastic={0.9}
      onDragEnd={handleDragEnd}
      initial={{ scale: isTop ? 1 : 0.92, y: isTop ? 0 : 20 }}
      animate={{ scale: isTop ? 1 : 0.92, y: isTop ? 0 : 20 }}
      data-testid={isTop ? "swipe-card-top" : "swipe-card-stack"}
    >
      {/* Background Image - Full card */}
      <img
        src={restaurant.photo_url || "https://images.pexels.com/photos/262978/pexels-photo-262978.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"}
        alt={restaurant.name}
        className="w-full h-full object-cover"
        draggable={false}
      />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

      {/* Like Indicator */}
      <motion.div
        className="absolute top-1/3 right-6 px-6 py-3 rounded-xl border-4 border-green-500 bg-green-500/20"
        style={{ opacity: likeOpacity, rotate: 12 }}
      >
        <span className="text-3xl font-black text-green-500">LIKE</span>
      </motion.div>

      {/* Nope Indicator */}
      <motion.div
        className="absolute top-1/3 left-6 px-6 py-3 rounded-xl border-4 border-red-500 bg-red-500/20"
        style={{ opacity: nopeOpacity, rotate: -12 }}
      >
        <span className="text-3xl font-black text-red-500">NOPE</span>
      </motion.div>

      {/* Social Signal */}
      {socialSignal && isTop && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute top-4 left-1/2 -translate-x-1/2 bg-forkyeah-accent px-4 py-1.5 rounded-full shadow-lg"
        >
          <span className="text-xs font-bold text-forkyeah-main flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            {socialSignal}
          </span>
        </motion.div>
      )}

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-5">
        {/* Vibe Tag */}
        <div className="mb-2">
          <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-bold text-white">
            {vibeTag}
          </span>
        </div>

        {/* Restaurant Name */}
        <h2 className="text-3xl font-black text-white mb-1 drop-shadow-lg leading-tight">
          {restaurant.name}
        </h2>

        {/* Cuisine */}
        <p className="text-white/80 font-semibold text-sm mb-3">
          {restaurant.cuisine_type}
        </p>

        {/* Secondary Info - Smaller */}
        <div className="flex items-center gap-3 text-white/70 text-xs font-semibold">
          {restaurant.rating && (
            <span className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
              {restaurant.rating.toFixed(1)}
            </span>
          )}
          <span className="text-green-400">{priceDisplay}</span>
          {restaurant.distance !== null && restaurant.distance !== undefined && (
            <span>{restaurant.distance} km</span>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function SwipePage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [swiping, setSwiping] = useState(false);
  const [timeLeft, setTimeLeft] = useState(SESSION_DURATION);
  const [socialSignal, setSocialSignal] = useState(null);
  const [showMatchBuildup, setShowMatchBuildup] = useState(false);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  const participantId = sessionStorage.getItem("participantId");

  useEffect(() => {
    if (!participantId) {
      toast.error("Please join the session first");
      navigate(`/join/${sessionId}`);
      return;
    }

    const fetchRestaurants = async () => {
      try {
        const response = await axios.get(`${API}/sessions/${sessionId}/restaurants`);
        setRestaurants(response.data);
        startTimeRef.current = Date.now();
      } catch (error) {
        console.error("Failed to fetch restaurants:", error);
        toast.error("Failed to load restaurants");
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurants();
  }, [sessionId, participantId, navigate]);

  // Timer countdown
  useEffect(() => {
    if (loading || restaurants.length === 0) return;
    
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          // Time's up - navigate to results
          navigate(`/match/${sessionId}`);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [loading, restaurants.length, sessionId, navigate]);

  // Poll for matches and social signals
  useEffect(() => {
    const checkForMatches = async () => {
      try {
        const response = await axios.get(`${API}/sessions/${sessionId}/status`);
        
        // Show social signals randomly
        if (Math.random() > 0.7 && currentIndex < restaurants.length) {
          const signals = [
            "2 people liked this",
            "Someone just passed",
            "You're close to a match!",
            "Popular choice",
          ];
          setSocialSignal(signals[Math.floor(Math.random() * signals.length)]);
          setTimeout(() => setSocialSignal(null), 2500);
        }
        
        if (response.data.latest_match) {
          // Show buildup animation
          setShowMatchBuildup(true);
          
          const elapsedSeconds = Math.round((Date.now() - startTimeRef.current) / 1000);
          sessionStorage.setItem("latestMatch", JSON.stringify({
            ...response.data.latest_match,
            elapsed_time: elapsedSeconds,
          }));
          
          setTimeout(() => {
            navigate(`/match/${sessionId}`);
          }, 2000);
        }
      } catch (error) {
        console.error("Error checking for matches:", error);
      }
    };

    const interval = setInterval(checkForMatches, 3000);
    return () => clearInterval(interval);
  }, [sessionId, navigate, currentIndex, restaurants.length]);

  const handleSwipe = useCallback(
    async (direction) => {
      if (swiping) return;
      setSwiping(true);

      const restaurant = restaurants[currentIndex];

      try {
        const response = await axios.post(`${API}/swipes`, {
          session_id: sessionId,
          participant_id: participantId,
          restaurant_id: restaurant.id,
          restaurant_name: restaurant.name,
          direction,
        });

        if (response.data.match) {
          // Show buildup animation
          setShowMatchBuildup(true);
          
          const elapsedSeconds = Math.round((Date.now() - startTimeRef.current) / 1000);
          sessionStorage.setItem("latestMatch", JSON.stringify({
            ...response.data.match,
            restaurant_photo: restaurant.photo_url,
            restaurant_address: restaurant.address,
            restaurant_rating: restaurant.rating,
            elapsed_time: elapsedSeconds,
          }));
          
          setTimeout(() => {
            navigate(`/match/${sessionId}`);
          }, 2000);
          return;
        }

        setCurrentIndex((prev) => prev + 1);
      } catch (error) {
        console.error("Failed to record swipe:", error);
        toast.error("Swipe failed");
      } finally {
        setSwiping(false);
      }
    },
    [currentIndex, restaurants, sessionId, participantId, navigate, swiping]
  );

  const handleButtonSwipe = (direction) => {
    if (currentIndex >= restaurants.length) return;
    handleSwipe(direction);
  };

  // Match buildup overlay
  if (showMatchBuildup) {
    return (
      <main className="app-container">
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-center"
          >
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 0.8 }}
              className="w-20 h-20 rounded-full bg-forkyeah-primary/20 flex items-center justify-center mx-auto mb-4"
            >
              <Sparkles className="w-10 h-10 text-forkyeah-primary" />
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-xl font-black text-forkyeah-main"
            >
              Checking for overlap...
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="text-forkyeah-primary font-bold mt-2"
            >
              You're close!
            </motion.p>
          </motion.div>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="app-container">
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <div className="w-10 h-10 border-4 border-forkyeah-primary border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-forkyeah-muted font-bold">Loading restaurants...</p>
        </div>
      </main>
    );
  }

  if (currentIndex >= restaurants.length) {
    return (
      <main className="app-container">
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <div className="w-20 h-20 rounded-full bg-forkyeah-accent/30 flex items-center justify-center mx-auto mb-4">
              <Heart className="w-10 h-10 text-forkyeah-primary" />
            </div>
            <h2 className="text-2xl font-black text-forkyeah-main mb-2">All done!</h2>
            <p className="text-forkyeah-muted font-semibold mb-4">
              Waiting for others to finish...
            </p>
            <div className="flex items-center justify-center gap-2">
              <div className="w-2 h-2 bg-forkyeah-primary rounded-full animate-bounce" />
              <div className="w-2 h-2 bg-forkyeah-primary rounded-full animate-bounce" style={{ animationDelay: "0.1s" }} />
              <div className="w-2 h-2 bg-forkyeah-primary rounded-full animate-bounce" style={{ animationDelay: "0.2s" }} />
            </div>
          </motion.div>
        </div>
      </main>
    );
  }

  const visibleCards = restaurants.slice(currentIndex, currentIndex + 2);
  const progress = ((currentIndex + 1) / restaurants.length) * 100;

  return (
    <main className="app-container">
      <div className="flex-1 flex flex-col">
        {/* Header with timer and progress */}
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className={`w-4 h-4 ${timeLeft < 30 ? 'text-red-500' : 'text-forkyeah-muted'}`} />
            <span className={`text-sm font-bold ${timeLeft < 30 ? 'text-red-500' : 'text-forkyeah-muted'}`}>
              {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
            </span>
          </div>
          
          <div className="flex items-center gap-1.5 bg-forkyeah-background px-3 py-1 rounded-full">
            <span className="text-xs font-bold text-forkyeah-main">
              {currentIndex + 1} of {restaurants.length}
            </span>
          </div>
        </div>

        {/* Progress bar */}
        <div className="px-4 mb-2">
          <div className="h-1 bg-forkyeah-background rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-forkyeah-primary"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        {/* Cards Container */}
        <div className="flex-1 relative px-4 pb-2">
          <div className="relative w-full h-full max-h-[55vh]">
            <AnimatePresence>
              {visibleCards.map((restaurant, index) => (
                <SwipeCard
                  key={restaurant.id}
                  restaurant={restaurant}
                  onSwipe={handleSwipe}
                  isTop={index === 0}
                  socialSignal={index === 0 ? socialSignal : null}
                />
              ))}
            </AnimatePresence>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="px-6 pb-6 flex items-center justify-center gap-6 relative z-20">
          <motion.button
            data-testid="swipe-left-btn"
            onClick={() => handleButtonSwipe("left")}
            className="w-16 h-16 rounded-full bg-white shadow-lg flex items-center justify-center border-2 border-red-100 active:scale-90 transition-transform"
            whileTap={{ scale: 0.9 }}
          >
            <X className="w-8 h-8 text-red-500" strokeWidth={3} />
          </motion.button>
          <motion.button
            data-testid="swipe-right-btn"
            onClick={() => handleButtonSwipe("right")}
            className="w-16 h-16 rounded-full bg-white shadow-lg flex items-center justify-center border-2 border-green-100 active:scale-90 transition-transform"
            whileTap={{ scale: 0.9 }}
          >
            <Heart className="w-8 h-8 text-green-500" strokeWidth={2.5} />
          </motion.button>
        </div>
      </div>
    </main>
  );
}
